import type { Locale } from "@/lib/i18n";

/**
 * City landing pages served at `/[locale]/ai-development-agency-company-{slug}`
 * by the shared bare-slug route (`app/[locale]/(root-slug)/[slug]`).
 *
 * These are service-area pages (Woltrio's office is in Rawalpindi — see the
 * footer contact block), not branch offices, so surface them under an
 * "areas we serve" label rather than "locations".
 *
 * Each list is country-specific and its `_data/locations/*.json` is authored
 * for exactly one locale, so the footer only surfaces the list matching the
 * active locale (`locationLinksByLocale`). Locales without a list (en-ca,
 * en-au, fr-fr) render no strip at all.
 *
 * City names are proper nouns and stay untranslated; only the section label is
 * localized (`t.footer.areasServedTitle`).
 */
export interface LocationLink {
  city: string;
  href: string;
}

/** United States — served on en-us. */
export const locationLinks: LocationLink[] = [
  { city: "Atlanta", href: "/ai-development-agency-company-atlanta" },
  { city: "Austin", href: "/ai-development-agency-company-austin" },
  { city: "Boston", href: "/ai-development-agency-company-boston" },
  { city: "Chicago", href: "/ai-development-agency-company-chicago" },
  { city: "Dallas", href: "/ai-development-agency-company-dallas" },
  { city: "Denver", href: "/ai-development-agency-company-denver" },
  { city: "Houston", href: "/ai-development-agency-company-houston" },
  { city: "New York", href: "/ai-development-agency-company-new-york" },
  { city: "San Francisco", href: "/ai-development-agency-company-san-francisco" },
  { city: "Seattle", href: "/ai-development-agency-company-seattle" },
];

/** United Kingdom — served on en-gb. */
export const ukLocationLinks: LocationLink[] = [
  { city: "Birmingham", href: "/ai-development-agency-company-birmingham" },
  { city: "Bristol", href: "/ai-development-agency-company-bristol" },
  { city: "Cambridge", href: "/ai-development-agency-company-cambridge" },
  { city: "Edinburgh", href: "/ai-development-agency-company-edinburgh" },
  { city: "Glasgow", href: "/ai-development-agency-company-glasgow" },
  { city: "Leeds", href: "/ai-development-agency-company-leeds" },
  { city: "Liverpool", href: "/ai-development-agency-company-liverpool" },
  { city: "London", href: "/ai-development-agency-company-london" },
  { city: "Manchester", href: "/ai-development-agency-company-manchester" },
  { city: "Newcastle", href: "/ai-development-agency-company-newcastle" },
];

/** Germany — served on de-de. */
export const deLocationLinks: LocationLink[] = [
  { city: "Berlin", href: "/ai-development-agency-company-berlin" },
  { city: "Dortmund", href: "/ai-development-agency-company-dortmund" },
  { city: "Düsseldorf", href: "/ai-development-agency-company-duesseldorf" },
  { city: "Frankfurt am Main", href: "/ai-development-agency-company-frankfurt" },
  { city: "Hamburg", href: "/ai-development-agency-company-hamburg" },
  { city: "Hannover", href: "/ai-development-agency-company-hannover" },
  { city: "Köln", href: "/ai-development-agency-company-koeln" },
  { city: "Leipzig", href: "/ai-development-agency-company-leipzig" },
  { city: "München", href: "/ai-development-agency-company-muenchen" },
  { city: "Stuttgart", href: "/ai-development-agency-company-stuttgart" },
];

export const locationLinksByLocale: Partial<Record<Locale, LocationLink[]>> = {
  "en-us": locationLinks,
  "en-gb": ukLocationLinks,
  "de-de": deLocationLinks,
};

/**
 * City pages that exist in exactly one locale, keyed by path.
 *
 * The UK and German pages have no `en-us` entry in their `_data` JSON, so
 * `getPageData`'s en-us fallback finds nothing and the route calls
 * `notFound()`. Switching language while on one of them would otherwise land
 * the user on a 404, so `LanguageSwitcher` looks the path up here and sends
 * them to the target locale's homepage instead.
 *
 * The US pages are deliberately absent: their JSON has an `en-us` key, which
 * every other locale falls back to, so they render in all six.
 *
 * Derived from the arrays above — adding a city to one of those lists keeps
 * this in sync automatically.
 */
export const singleLocaleLocationPaths: Record<string, Locale> =
  Object.fromEntries([
    ...ukLocationLinks.map((l) => [l.href, "en-gb" as Locale]),
    ...deLocationLinks.map((l) => [l.href, "de-de" as Locale]),
  ]);
